<template>
  <div
    v-show="showTasks"
    ref="columnRef"
    :class="{ 'pointer-events-none': isLoading }"
    class="kanban-column flex flex-col overflow-x-hidden transition-all"
  >
    <div class="p-2 mb-2">
      <StatusBanner
        :status="status"
        :count="totalTasks"
        :loading="isInitiallyLoading"
        type="kanban"
      />
    </div>
    <div
      ref="cards"
      :data-status="status"
      class="cards flex flex-col h-full overflow-scroll no-scrollbar mb-2"
    >
      <template v-if="!isInitiallyLoading">
        <TasksKanbanCard
          v-for="task in tasks"
          :key="task.id"
          :task="task"
          @click:task="(task) => emit('click:task', task)"
          class="my-1.5 mx-2 first:mt-0 last:mb-0"
        />
        <span
          v-if="!isLastPage"
          ref="loadMoreTrigger"
          class="w-full h-px block"
        />
      </template>
      <template v-if="isLoading">
        <div
          v-for="pill in Math.floor(Math.random() * 5) + 1"
          :key="pill"
          class="px-2"
        >
          <SkeletonTasksKanbanCard class="mb-2" />
        </div>
      </template>
    </div>
  </div>
</template>

<script setup>
// Import Vue Utilities
import { inject, ref, onMounted, watch, computed, onUnmounted } from "vue";
import { useIntersectionObserver } from "@vueuse/core";

// Import Utilities
import axios from "axios";
import route from "ziggy-js";
import { router } from "@inertiajs/vue3";
import { storeToRefs } from "pinia";
import { uniqBy, isEmpty } from "lodash";
import Sortable from "sortablejs";
import { useI18n } from "vue-i18n";

// Import Components
import { StatusBanner } from "@/SharedComponents/Tasks";
import TasksKanbanCard from "./TasksKanbanCard.vue";
import SkeletonTasksKanbanCard from "./SkeletonTasksKanbanCard.vue";
import { toast } from "@/SharedComponents/Toaster";

// Import Composables
import { useSorting } from "@/Composables/useSorting.js";
import { useTouchscreen } from "@/Composables/useTouchscreen";
import { useSearch } from "@/Composables/useSearch";
import { useFilters } from "@/Composables/useFilters";
import { useDefaultTasksViews } from "@/Pages/Tasks/Views";
import { useTaskEvents } from "@/Composables/useTaskEvents";

// Emitter
const emitter = inject("emitter");

// Filters: Global
const globalFilterStore = useFilters("global");
const { filterValues: globalFilterValues } = storeToRefs(globalFilterStore);

// Filters: Tasks
const filterStore = useFilters("tasks");
const { filterValues, activeView } = storeToRefs(filterStore);

// Sorting
const sortingStore = useSorting("tasks");
const { sortingValues } = storeToRefs(sortingStore);

// Search
const searchStore = useSearch("tasks");
const { searchValues } = storeToRefs(searchStore);

// Touchscreen
const { isTouchscreen } = useTouchscreen();

// Default views
const { DefaultView } = useDefaultTasksViews();

// Emits
const emit = defineEmits([
  "click:task",
  "update:loading",
  "update:allTasks",
  "update:dragging",
  "refresh:kanban",
]);

// Define Props
const props = defineProps({
  status: {
    type: String,
    required: true,
  },
  allTasks: {
    type: Array,
    required: true,
  },
  disableDragging: {
    type: Boolean,
    required: false,
    default: false,
  },
});

// Refs
const tasks = ref([]);
const page = ref(1);
const columnRef = ref(null);
const cards = ref(null);
const isInitiallyLoading = ref(true);
const isLoading = ref(false);
const isLastPage = ref(false);
const previousList = ref([]);
const totalTasks = ref(0);
const sortableInstance = ref(null);
const isIntersectingLoadMoreTrigger = ref(false);
const loadMoreTrigger = ref(null);

// Computed Props
const filters = computed(() => {
  return {
    ...sortingValues.value,
    ...searchValues.value,
    ...filterValues.value,
    tenant: globalFilterValues.value.tenant,
    tenant_only: filterValues.value.tenant?.length > 0,
    archived_only: activeView.value.id === DefaultView.ARCHIVED.id,
  };
});

const filtersWithStatus = computed(() => {
  return {
    ...filters.value,
    status: [props.status],
  };
});

useTaskEvents({
  tasks,
  filters: filtersWithStatus,
  isLastPage,
  callback: (change) => {
    if (isLoading.value) {
      return;
    }

    totalTasks.value = totalTasks.value + change;
    emit("update:loading", {
      status: props.status,
      fetchingStatus: "loaded",
      count: totalTasks.value,
    });
  },
});

const includeStatus = computed(() => {
  const { status } = filters.value;
  return !status || isEmpty(status) || status.includes(props.status);
});

const showTasks = computed(() => {
  return includeStatus.value;
});

// Functions
const { stop } = useIntersectionObserver(
  loadMoreTrigger,
  ([{ isIntersecting }]) => {
    isIntersectingLoadMoreTrigger.value = isIntersecting;
    // Don't trigger multiple times while running
    if (isIntersecting && !isLoading.value) {
      getTasks();
    }
  }
);

const { t } = useI18n();
const { hasPermission, permissions } = inject("permissions");

const initializeSortable = () => {
  sortableInstance.value = new Sortable(cards.value, {
    // Disable sorting within a list.
    sort: false,
    group: {
      name: props.status,
      pull: true,
      put: true,
    },
    draggable: ".kanban-card",
    forceFallback: true,
    delay: isTouchscreen.value ? 300 : 0,
    onStart: function () {
      // Get the list.
      const list = cards.value;

      emit("update:dragging", true);

      // Save the current list in the previousList.
      previousList.value = [...list.children].filter(
        (item) => !item.classList.contains("sortable-drag")
      );
    },
    onAdd: function (event) {
      const task = props.allTasks
        .filter((task) => task.id == event.item.id)
        .shift();

      // Get the list.
      const list = cards.value;

      // Save the current list in the previousList.
      previousList.value = [...list.children].filter(
        (item) => item.id !== task.id
      );
    },
    onEnd: function (event) {
      const from = event.from.dataset.status;
      const to = event.to.dataset.status;
      const task = props.allTasks
        .filter((task) => task.id == event.item.id)
        .shift();

      if (
        to === "completed" &&
        !hasPermission(permissions.completeTask, task.tenant.id)
      ) {
        emitter.emit("undo-drag-action", { from, to });
        toast.error(t("toasts.no_permission.tasks.complete"));
      } else if (
        to === "open" &&
        hasPermission(permissions.limitedAccess, task.tenant.id)
      ) {
        emitter.emit("undo-drag-action", { from, to });
        toast.error(t("toasts.no_permission.tasks.limited_access"));
      } else if (from !== to) {
        emitter.emit("change-status", { task, from, to });
      }

      emit("update:dragging", false);
    },
    onMove: function (event) {
      event.to.prepend(event.dragged);
    },
  });
};

const subscriptionKanban = (status = "all") => {
  if (status === "all" || status === props.status) {
    open.value = false;
    tasks.value = [];
    isLastPage.value = false;
    page.value = 1;
    getTasks();
  }
};

async function getTasks() {
  if (isLastPage.value === true || isLoading.value === true) {
    return;
  }

  // Update the isLoading
  isLoading.value = true;

  // Emit loading
  emit("update:loading", {
    status: props.status,
    fetchingStatus: "loading",
    count: totalTasks.value,
  });

  // Parameters
  const parameters = {
    page: page.value,
    ...filters.value,
    status: [props.status],
    per_page: 10,
  };

  const url = route("api.tasks.search");

  await axios
    .post(url, parameters)
    .then((response) => {
      // Append the newly retrieved tasks to the list of already loaded tasks.
      tasks.value = uniqBy([...tasks.value, ...response.data.data], "id");

      // Assign the total tasks to totalTasks.
      totalTasks.value = response.data.meta.total;

      // Increment the page number to load the next batch of tasks.
      page.value = page.value + 1;

      // There are no more subsequent pages to load.
      if (response.data.links.next === null) {
        isLastPage.value = true;
      }

      // Emit update:loading
      emit("update:loading", {
        status: props.status,
        fetchingStatus: "loaded",
        count: totalTasks.value,
      });

      // Update allTasks
      emit("update:allTasks", tasks.value);

      // Give everything time to re-render before checking again
      setTimeout(() => {
        if (isIntersectingLoadMoreTrigger.value) {
          getTasks();
        }
      }, 100);
    })
    .catch((err) => {
      console.log("error occurred", err);

      // Assign the total tasks to totalTasks.
      totalTasks.value = 0;

      // Emit loading
      emit("update:loading", {
        status: props.status,
        fetchingStatus: "error",
        count: totalTasks.value,
      });
    })
    .finally(() => {
      // Update the isInitiallyLoading
      isInitiallyLoading.value = false;

      // Update the isLoading
      isLoading.value = false;
    });
}

const completeTask = (task, from, to) => {
  router.post(
    route("tasks.complete", task.id),
    {},
    {
      onSuccess: () => emitter.emit("update-column", { task, from, to }),
    }
  );
};

const scheduleTask = (task, assignees, date, from, to) => {
  router.post(
    route("tasks.schedule", task.id),
    {
      assignee_id: assignees,
      schedule_for: date,
    },
    {
      onSuccess: () => emitter.emit("update-column", { task, from, to }),
    }
  );
};

const unscheduleTask = (task, from, to) => {
  router.put(
    route("tasks.update", task.id),
    {
      assignee_id: null,
      schedule_for: null,
      priority: task.priority?.id,
      is_private: task.problem?.is_private,
      description: task.description,
      category_id: task.category?.id,
    },
    {
      onSuccess: () => emitter.emit("update-column", { task, from, to }),
    }
  );
};

const rescheduleTask = (task, from, to) => {
  router.post(
    route("tasks.reopen", task.id),
    {},
    {
      onSuccess: () => emitter.emit("update-column", { task, from, to }),
    }
  );
};

const reopenTask = (task, from, to) => {
  router.post(
    route("tasks.reopen", task.id),
    {
      unschedule: true,
    },
    {
      onSuccess: () => {
        emitter.emit("update-column", { task, from, to });
      },
    }
  );
};

// Watchers
watch(cards, () => {
  initializeSortable();
});

watch(
  filters,
  () => {
    isInitiallyLoading.value = true;
    open.value = false;
    tasks.value = [];
    isLastPage.value = false;
    page.value = 1;

    getTasks();
  },
  { deep: true }
);

watch(props, () => {
  if (!sortableInstance.value) {
    return;
  }

  sortableInstance.value.option("disabled", props.disableDragging);
});

// Lifecycle Hooks
onMounted(() => {
  getTasks();
});

onUnmounted(() => {
  emitter.off("reload-tasks", subscriptionKanban);
  stop();
});

emitter.on("change-status", (data) => {
  const { task, from, to } = data;

  // Update the loading
  isLoading.value = from === to ? false : true;

  if (props.status === to) {
    // Move task from 'open' to 'scheduled'.
    if (from === "open" && to === "scheduled") {
      emitter.emit("scheduling-task", task);

      emitter.on("confirm-scheduling-task", (data) => {
        emitter.off("confirm-scheduling-task");
        emitter.off("cancel-scheduling-task");
        const { assignees } = data;
        let { date } = data;

        if (date) {
          date = new Date(date.setHours(23, 59, 59));
        }

        scheduleTask(task, assignees, date, from, to);
      });

      emitter.on("cancel-scheduling-task", () => {
        emitter.off("confirm-scheduling-task");
        emitter.off("cancel-scheduling-task");
        emitter.emit("undo-drag-action", { from, to });
      });
    }

    // Move task from 'scheduled' to 'open'.
    if (from === "scheduled" && to === "open") {
      unscheduleTask(task, from, to);
    }

    // Move task from 'open' or 'scheduled' to 'completed'.
    if ((from === "open" || from === "scheduled") && to === "completed") {
      completeTask(task, from, to);
    }

    // Move task from 'completed' to 'scheduled'.
    if (from === "completed" && to === "scheduled") {
      rescheduleTask(task, from, to);
    }

    // Move task from 'completed' to 'open'.
    if (from === "completed" && to === "open") {
      reopenTask(task, from, to);
    }
  }
});

emitter.on("update-column", (data) => {
  if (props.status !== "completed") {
    const { from, to } = data;

    // Decrement the totalTasks from the 'from' column.
    if (from == props.status) {
      totalTasks.value--;
    }

    // Increment the totalTasks from the 'to' column.
    if (to == props.status) {
      totalTasks.value++;
    }
  }

  // Update  this isLoading.
  isLoading.value = false;
});

emitter.on("undo-drag-action", (data) => {
  const { from, to } = data;

  if (props.status === from || props.status === to) {
    emit("refresh:kanban");
  }

  // Update  this isLoading.
  isLoading.value = false;
});
</script>
